import {
  GRAFTCODE_ONBOARDING_GOAL,
  GRAFTCODE_ONBOARDING_PHASE,
  GRAFTCODE_ONBOARDING_SCENARIO,
  GRAFTCODE_ONBOARDING_SETUP_MODE,
  GRAFTCODE_ONBOARDING_WIZARD_STEP,
  resolveSetupModeForGoal,
} from './graftcode-onboarding.const'
import { detectGuideOsFromUserAgent } from './lib/detectGuideOsFromUserAgent'
import { graftcodeOnboardingInitialState } from './stores/graftcode-onboarding.slice'
import type { GraftcodeOnboardingStoreState } from './stores/graftcode-onboarding.type'

const STORAGE_KEY = 'graftcode-onboarding-v1'

const VALID_GOAL_IDS = new Set(Object.values(GRAFTCODE_ONBOARDING_GOAL))
const VALID_SCENARIO_IDS = new Set(Object.values(GRAFTCODE_ONBOARDING_SCENARIO))
const VALID_SETUP_MODES = new Set(Object.values(GRAFTCODE_ONBOARDING_SETUP_MODE))

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === 'string')
}

function sanitizePersistedState(
  parsed: Partial<PersistedGraftcodeOnboardingState>
): Partial<PersistedGraftcodeOnboardingState> {
  const sanitized: Partial<PersistedGraftcodeOnboardingState> = {
    wizardCompleted: parsed.wizardCompleted === true,
    tutorialCompleted: parsed.tutorialCompleted === true,
    tutorialVideoHidden: parsed.tutorialVideoHidden === true,
    guideOsManual: parsed.guideOsManual === true,
  }

  if (parsed.goalId && VALID_GOAL_IDS.has(parsed.goalId)) {
    sanitized.goalId = parsed.goalId
  }
  if (parsed.scenarioId && VALID_SCENARIO_IDS.has(parsed.scenarioId)) {
    sanitized.scenarioId = parsed.scenarioId
  }
  if (parsed.primaryScenarioId && VALID_SCENARIO_IDS.has(parsed.primaryScenarioId)) {
    sanitized.primaryScenarioId = parsed.primaryScenarioId
  }
  if (parsed.setupMode && VALID_SETUP_MODES.has(parsed.setupMode)) {
    sanitized.setupMode = parsed.setupMode
  }
  if (typeof parsed.selectedStackId === 'string') {
    sanitized.selectedStackId = parsed.selectedStackId
  }
  if (typeof parsed.lastCompletedStackId === 'string') {
    sanitized.lastCompletedStackId = parsed.lastCompletedStackId
  }
  if (typeof parsed.activeStepIndex === 'number' && Number.isFinite(parsed.activeStepIndex)) {
    sanitized.activeStepIndex = parsed.activeStepIndex
  }
  if (isStringArray(parsed.completedScenarioIds)) {
    sanitized.completedScenarioIds = parsed.completedScenarioIds.filter((id) =>
      VALID_SCENARIO_IDS.has(id as (typeof GRAFTCODE_ONBOARDING_SCENARIO)[keyof typeof GRAFTCODE_ONBOARDING_SCENARIO])
    )
  }
  if (parsed.completedStacksByScenario && typeof parsed.completedStacksByScenario === 'object') {
    const stacks: PersistedGraftcodeOnboardingState['completedStacksByScenario'] = {}
    for (const [key, value] of Object.entries(parsed.completedStacksByScenario)) {
      if (
        VALID_SCENARIO_IDS.has(
          key as (typeof GRAFTCODE_ONBOARDING_SCENARIO)[keyof typeof GRAFTCODE_ONBOARDING_SCENARIO]
        ) &&
        typeof value === 'string'
      ) {
        stacks[key as keyof typeof stacks] = value
      }
    }
    sanitized.completedStacksByScenario = stacks
  }
  if (typeof parsed.guideOs === 'string') {
    sanitized.guideOs = parsed.guideOs as PersistedGraftcodeOnboardingState['guideOs']
  }

  return sanitized
}

export type PersistedGraftcodeOnboardingState = Pick<
  GraftcodeOnboardingStoreState,
  | 'wizardCompleted'
  | 'goalId'
  | 'scenarioId'
  | 'selectedStackId'
  | 'setupMode'
  | 'activeStepIndex'
  | 'tutorialCompleted'
  | 'tutorialVideoHidden'
  | 'completedScenarioIds'
  | 'primaryScenarioId'
  | 'lastCompletedStackId'
  | 'completedStacksByScenario'
  | 'guideOs'
  | 'guideOsManual'
>

export function loadPersistedGraftcodeOnboardingState(): Partial<PersistedGraftcodeOnboardingState> | null {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw) as Partial<PersistedGraftcodeOnboardingState>
    return sanitizePersistedState(parsed)
  } catch {
    return null
  }
}

export function persistGraftcodeOnboardingState(state: GraftcodeOnboardingStoreState): void {
  if (typeof window === 'undefined') {
    return
  }

  const payload: PersistedGraftcodeOnboardingState = {
    wizardCompleted: state.wizardCompleted,
    goalId: state.goalId,
    scenarioId: state.scenarioId,
    selectedStackId: state.selectedStackId,
    setupMode: state.setupMode,
    activeStepIndex: state.activeStepIndex,
    tutorialCompleted: state.tutorialCompleted,
    tutorialVideoHidden: state.tutorialVideoHidden,
    completedScenarioIds: state.completedScenarioIds,
    primaryScenarioId: state.primaryScenarioId,
    lastCompletedStackId: state.lastCompletedStackId,
    completedStacksByScenario: state.completedStacksByScenario,
    guideOs: state.guideOs,
    guideOsManual: state.guideOsManual,
  }

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  } catch {
    // ignore quota errors
  }
}

export function clearPersistedGraftcodeOnboardingState(): void {
  if (typeof window === 'undefined') {
    return
  }
  window.localStorage.removeItem(STORAGE_KEY)
}

export function mergePersistedGraftcodeOnboardingState(
  persisted: Partial<GraftcodeOnboardingStoreState> | null
): GraftcodeOnboardingStoreState {
  if (!persisted) {
    return {
      ...graftcodeOnboardingInitialState,
      guideOs: detectGuideOsFromUserAgent(),
      hydrated: true,
    }
  }

  const goalId = persisted.goalId ?? graftcodeOnboardingInitialState.goalId

  const guideOsManual = persisted.guideOsManual ?? false

  const completedScenarioIds = persisted.completedScenarioIds ?? []
  const backfilledCompleted =
    persisted.tutorialCompleted && persisted.scenarioId && !completedScenarioIds.includes(persisted.scenarioId)
      ? [...completedScenarioIds, persisted.scenarioId]
      : completedScenarioIds

  const completedStacksByScenario = { ...(persisted.completedStacksByScenario ?? {}) }
  if (
    persisted.tutorialCompleted &&
    persisted.scenarioId &&
    persisted.selectedStackId &&
    !completedStacksByScenario[persisted.scenarioId]
  ) {
    completedStacksByScenario[persisted.scenarioId] = persisted.selectedStackId
  }

  return {
    ...graftcodeOnboardingInitialState,
    ...persisted,
    goalId,
    completedScenarioIds: backfilledCompleted,
    primaryScenarioId: persisted.primaryScenarioId ?? persisted.scenarioId ?? null,
    lastCompletedStackId:
      persisted.lastCompletedStackId ??
      (persisted.scenarioId ? completedStacksByScenario[persisted.scenarioId] : null) ??
      persisted.selectedStackId ??
      null,
    completedStacksByScenario,
    setupMode: persisted.setupMode ?? resolveSetupModeForGoal(goalId),
    guideOs: guideOsManual
      ? (persisted.guideOs ?? graftcodeOnboardingInitialState.guideOs)
      : detectGuideOsFromUserAgent(),
    guideOsManual,
    phase: GRAFTCODE_ONBOARDING_PHASE.idle,
    wizardStep: GRAFTCODE_ONBOARDING_WIZARD_STEP.goal,
    hydrated: true,
  }
}
